import React, { CSSProperties, useEffect, useRef } from "react";
import { Trans } from "react-i18next";
import { ProcessusDefinition } from "types/Processus";

import { tw } from "twind";
import ConfirmationModal from "./ConfirmationModal";
import { Pojo } from "types/Galaxy";
import { Fa } from "composants/Icon";
import { track } from "tracking";
import { useInfoProcessus } from "features/processus/processusManager";
import { ButtonLink, Columns, Column } from "@axin-org/comet";
import DelayProcessModal from "./DelayProcessModal";
import { useProcessus } from "./useProcessus";
import classNames from "classnames";

export function ProcessusLoading(props: { id: string }) {
  const [processusState] = useInfoProcessus(props.id);

  return !processusState.matches("done") &&
    !processusState.matches("wait") &&
    !processusState.matches("error") ? (
    <span className="icon">
      <Fa icon="spinner" spin />
    </span>
  ) : null;
}

export interface ProcessusLinkProps {
  definition: ProcessusDefinition;
  canDelayProcess?: Boolean;
  className?: string;
  style?: CSSProperties;
  setShowConfirmModal?: (modal: {
    id: string;
    show: boolean;
    position: { x: number; y: number } | null;
    launchProcess: () => void;
    close: () => void;
    buttonRef: React.RefObject<HTMLDivElement | null>;
  }) => void;
}

export function ProcessusLink({
  definition,
  children,
  canDelayProcess,
  className,
  setShowConfirmModal,
  disabled,
  ...restProps
}: React.ComponentProps<"a"> & { disabled?: boolean } & ProcessusLinkProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const processus = useProcessus(definition);

  // Machine à gaz pour gérer le cas des process sur un élément dirty sur une AGGrid
  // On remonte le showConfirmModal jusqu'à AGGridContainer afin d'afficher une autre fenêtre, car le menu se ferme au moment du clic.
  // Donc cele-ci n'est pas render dans ce cas précis.
  useEffect(() => {
    setShowConfirmModal &&
      setShowConfirmModal({
        id: definition.id,
        show: processus.modal.confirm.show,
        position: processus.modal.position,
        launchProcess: (editionType?: "rapide" | "apercu" | undefined, forcedSelected?: Pojo[]) => {
          processus.launch(editionType, forcedSelected);
        },
        close: () => {
          processus.modal.confirm.set(false);
          track("processus::modal::cancel");
        },
        buttonRef: containerRef,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processus.modal.confirm.show, processus.modal.position]);

  return (
    <>
      {processus.modal.confirm.show && processus.modal.position && (
        <ConfirmationModal
          id={definition.id}
          launchProcess={processus.launch}
          close={() => {
            processus.modal.confirm.set(false);
            track("processus::modal::cancel");
          }}
          top={processus.modal.position.x}
          left={processus.modal.position.y}
          buttonRef={containerRef}
        />
      )}

      <div
        ref={containerRef}
        // {...(buttonProps as any)}
        className="processmenu-hover-show-action"
        style={restProps.style}
      >
        <Columns gap={0}>
          <Column width="full">
            <a
              onClick={disabled ? undefined : processus.button.onClick}
              className={classNames(
                tw("w-full"),
                disabled ? tw("cursor-not-allowed text-gray-400") : className,
              )}
              aria-disabled={disabled}
              {...restProps}
            >
              {children ?? definition.label}
              {processus.hasProcessusStateSpawned && (
                <ProcessusLoading id={processus.stableProcessusId} />
              )}
              {definition.type === "edition" && definition.apercu && definition.rapide && (
                <span className="processmenu-action">
                  <div className="field has-addons">
                    <div className="control">
                      <button
                        className="button is-small is-primary"
                        data-edition-type="apercu"
                        disabled={disabled || !definition.apercu}
                      >
                        <Trans i18nKey="commun_apercu" />
                      </button>
                    </div>
                    <div className="control">
                      <button
                        className="button is-small is-info"
                        data-edition-type="rapide"
                        disabled={disabled || !definition.rapide}
                      >
                        <Trans i18nKey="commun_rapide" />
                      </button>
                    </div>
                  </div>
                </span>
              )}
            </a>
          </Column>
          <Column className="flex justify-end">
            {definition.type !== "navigation" && definition.isDifferable && canDelayProcess && (
              <ButtonLink
                onClick={disabled ? undefined : () => processus.modal.calendar.set(true)}
                className="p-0 right-0"
              >
                <Fa icon="clock" />
              </ButtonLink>
            )}
          </Column>
        </Columns>
      </div>

      {processus.modal.calendar.show && (
        <DelayProcessModal
          sjmoCode={processus.context.sjmoCode}
          compositeID={definition.id}
          type={definition.type}
          editionType={processus.editionDefault}
          selected={processus.context.selected ?? []}
          advanced={
            (definition.type === "traitement" && definition.isAdvanced) ||
            (definition.type === "edition" && definition.isAdvanced)
          }
          label={definition.label}
          needEntity={definition.needEntity}
          isOneByOne={definition.isOneByOne}
          onClose={() => processus.modal.calendar.set(false)}
          callback={processus.context.onAfterSaveProcess}
          dtFilter={processus.context.dtFilter}
          dtFilterBar={processus.context.dtFilterBar}
          dtTableName={processus.context.dtTableName}
          selectAll={processus.context.selectAll}
          selectAllType={processus.context.selectAllType}
        />
      )}
    </>
  );
}

export default ProcessusLink;
